import React, { useCallback, useEffect, useRef, useState } from "react";
import { generateLiveInterviewAiQuestions } from "../api/liveInterviewApi";

const INITIAL_QUESTION_MODE = "mixed";
// The existing Meet API validates a bounded 1–5 range. Request the complete
// small question bank in one authorized call rather than treating three
// initial suggestions as the end of an interview.
const INITIAL_QUESTION_COUNT = 5;
const INITIAL_AUTO_INTENT = "initial_auto";
const MANUAL_RETRY_INTENT = "manual_retry";
const CONVERSATION_AUTO_INTENT = "conversation_auto";
const ADAPTIVE_QUESTION_COUNT = 3;
const WORKER_FINALS_PER_ADAPTIVE_BATCH = 6;
const MAX_ADAPTIVE_BATCHES = 3;
const SESSION_CACHE_LIMIT = 20;

// Session-only request/result state avoids duplicate paid dispatches from an
// ordinary remount, StrictMode replay, or drawer reopen for the same staff
// room, booking, and validated application.
const inFlightInitialQuestionRequests = new Map();
const initialQuestionSessionStates = new Map();

const isAbortError = (error) =>
  error?.name === "AbortError" || error?.code === "REQUEST_ABORTED";

const toSafeQuestions = (questions) =>
  (Array.isArray(questions) ? questions : [])
    .slice(0, 5)
    .map((question, index) => ({
      id: String(question?.id || `question-${index + 1}`),
      category: String(question?.category || "").trim(),
      question: String(question?.question || "").trim(),
      questionReference: String(question?.question_reference || "").trim(),
    }))
    .filter((question) => question.question);

const mergeQuestions = (current, additions) => {
  const references = new Set(
    current.map((question) => question.questionReference).filter(Boolean)
  );
  const text = new Set(current.map((question) => question.question.toLocaleLowerCase()));
  return additions.reduce((merged, question) => {
    const questionText = question.question.toLocaleLowerCase();
    if (
      text.has(questionText)
      || (question.questionReference && references.has(question.questionReference))
    ) {
      return merged;
    }
    text.add(questionText);
    if (question.questionReference) references.add(question.questionReference);
    merged.push(question);
    return merged;
  }, [...current]);
};

const getDisplayError = (error) => {
  if (error?.payload?.code === "ai_question_generation_in_progress") {
    return "Initial question suggestions are still being prepared.";
  }
  if (error?.payload?.code === "ai_question_generation_retry_available") {
    return "Initial question suggestions could not be generated. You may retry once.";
  }
  if (error?.status === 403) {
    return "AI question access is not available for this interview.";
  }
  if (error?.status === 429) {
    return "Question generation is temporarily limited. Please wait and try again.";
  }
  return "Question suggestions could not be generated. Please try again.";
};

const cacheSessionState = (scopeKey, state) => {
  if (initialQuestionSessionStates.has(scopeKey)) {
    initialQuestionSessionStates.delete(scopeKey);
  }
  initialQuestionSessionStates.set(scopeKey, state);
  while (initialQuestionSessionStates.size > SESSION_CACHE_LIMIT) {
    const oldestScope = initialQuestionSessionStates.keys().next().value;
    initialQuestionSessionStates.delete(oldestScope);
  }
};

const requestInitialQuestions = (scopeKey, roomId, applicationId, interviewerToken) => {
  const existing = inFlightInitialQuestionRequests.get(scopeKey);
  if (existing) return existing;

  const request = generateLiveInterviewAiQuestions(
    roomId,
    {
      applicationId,
      mode: INITIAL_QUESTION_MODE,
      questionCount: INITIAL_QUESTION_COUNT,
      generationIntent: INITIAL_AUTO_INTENT,
    },
    { interviewerToken }
  );
  inFlightInitialQuestionRequests.set(scopeKey, request);
  request.finally(() => {
    if (inFlightInitialQuestionRequests.get(scopeKey) === request) {
      inFlightInitialQuestionRequests.delete(scopeKey);
    }
  }).catch(() => {
    // The caller handles the rejection; this handler prevents the cleanup
    // chain from becoming an unhandled rejection.
  });
  return request;
};

const requestAdaptiveQuestions = (scopeKey, roomId, applicationId, interviewerToken, batch) => {
  const adaptiveScopeKey = `${scopeKey}::conversation-${batch}`;
  const existing = inFlightInitialQuestionRequests.get(adaptiveScopeKey);
  if (existing) return existing;

  const request = generateLiveInterviewAiQuestions(
    roomId,
    {
      applicationId,
      mode: INITIAL_QUESTION_MODE,
      questionCount: ADAPTIVE_QUESTION_COUNT,
      generationIntent: CONVERSATION_AUTO_INTENT,
    },
    { interviewerToken }
  );
  inFlightInitialQuestionRequests.set(adaptiveScopeKey, request);
  request.finally(() => {
    if (inFlightInitialQuestionRequests.get(adaptiveScopeKey) === request) {
      inFlightInitialQuestionRequests.delete(adaptiveScopeKey);
    }
  }).catch(() => {
    // The caller owns the result; avoid an unhandled cleanup rejection.
  });
  return request;
};

const AiCompanionPanel = ({
  roomId,
  bookingId = "",
  applicationId,
  actorRole = "",
  interviewerToken = "",
  verifiedWorkerFinalCount = 0,
}) => {
  const [questions, setQuestions] = useState([]);
  const [isMock, setIsMock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [canRetryInitialGeneration, setCanRetryInitialGeneration] = useState(false);
  const scopeVersionRef = useRef(0);
  const activeRequestRef = useRef(null);
  const isLoadingRef = useRef(false);
  const questionsRef = useRef([]);
  const requestedAdaptiveBatchesRef = useRef(new Set());

  const safeRoomId = String(roomId || "").trim();
  const safeApplicationId = String(applicationId || "").trim();
  const normalizedRole = String(actorRole || "").trim().toLowerCase();
  const isStaff = Boolean(safeRoomId && safeApplicationId) && normalizedRole !== "candidate";
  const scopeKey = [
    safeRoomId,
    String(bookingId || "").trim(),
    safeApplicationId,
    normalizedRole,
    String(interviewerToken || "").trim(),
  ].join("::");

  const generateQuestions = useCallback(async (generationIntent = MANUAL_RETRY_INTENT, adaptiveBatch = 0) => {
    if (!isStaff || isLoadingRef.current) return;

    const isAutomatic = generationIntent === INITIAL_AUTO_INTENT;
    const isAdaptive = generationIntent === CONVERSATION_AUTO_INTENT;
    const scopeVersion = scopeVersionRef.current;
    const controller = isAutomatic || isAdaptive ? null : new AbortController();
    if (!isAutomatic) activeRequestRef.current = controller;
    isLoadingRef.current = true;
    setIsLoading(true);
    setError("");
    setCanRetryInitialGeneration(false);

    try {
      const payload = isAutomatic
        ? await requestInitialQuestions(
          scopeKey,
          safeRoomId,
          safeApplicationId,
          interviewerToken
        )
        : isAdaptive
          ? await requestAdaptiveQuestions(
            scopeKey,
            safeRoomId,
            safeApplicationId,
            interviewerToken,
            adaptiveBatch
          )
        : await generateLiveInterviewAiQuestions(
          safeRoomId,
          {
            applicationId: safeApplicationId,
            mode: INITIAL_QUESTION_MODE,
            questionCount: INITIAL_QUESTION_COUNT,
            generationIntent,
          },
          {
            interviewerToken,
            signal: controller.signal,
          }
        );
      if (scopeVersionRef.current !== scopeVersion) return;

      const nextQuestions = mergeQuestions(
        isAdaptive ? questionsRef.current : [],
        toSafeQuestions(payload?.questions)
      );
      if (!nextQuestions.length) {
        const unavailable = "Question suggestions are unavailable. Please try again.";
        if (!isAdaptive) {
          questionsRef.current = [];
          setQuestions([]);
        }
        setIsMock(false);
        setError(unavailable);
        if (isAutomatic) cacheSessionState(scopeKey, { error: unavailable, canRetry: false });
        return;
      }
      questionsRef.current = nextQuestions;
      setQuestions(nextQuestions);
      setIsMock(payload?.mock === true);
      cacheSessionState(scopeKey, { questions: nextQuestions, isMock: payload?.mock === true });
    } catch (requestError) {
      if (scopeVersionRef.current !== scopeVersion || isAbortError(requestError)) return;
      const displayError = getDisplayError(requestError);
      const retryAvailable = isAutomatic
        && requestError?.payload?.code === "ai_question_generation_retry_available";
      if (!isAdaptive) {
        questionsRef.current = [];
        setQuestions([]);
        setIsMock(false);
      }
      setError(displayError);
      setCanRetryInitialGeneration(retryAvailable);
      if (isAutomatic) {
        cacheSessionState(scopeKey, { error: displayError, canRetry: retryAvailable });
      }
    } finally {
      if (scopeVersionRef.current === scopeVersion) {
        activeRequestRef.current = null;
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [interviewerToken, isStaff, safeApplicationId, safeRoomId, scopeKey]);

  useEffect(() => {
    scopeVersionRef.current += 1;
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    isLoadingRef.current = false;
    questionsRef.current = [];
    requestedAdaptiveBatchesRef.current = new Set();
    setQuestions([]);
    setIsMock(false);
    setIsLoading(false);
    setError("");
    setCanRetryInitialGeneration(false);

    if (!isStaff) return undefined;

    const cachedState = initialQuestionSessionStates.get(scopeKey);
    if (cachedState?.questions?.length) {
      questionsRef.current = cachedState.questions;
      setQuestions(cachedState.questions);
      setIsMock(cachedState.isMock === true);
      return undefined;
    }
    if (cachedState?.error) {
      setError(cachedState.error);
      setCanRetryInitialGeneration(cachedState.canRetry === true);
      return undefined;
    }

    void generateQuestions(INITIAL_AUTO_INTENT);
    return () => {
      scopeVersionRef.current += 1;
      activeRequestRef.current?.abort();
      activeRequestRef.current = null;
      isLoadingRef.current = false;
    };
  }, [generateQuestions, isStaff, scopeKey]);

  const workerFinalCount = Math.max(0, Number(verifiedWorkerFinalCount) || 0);
  const adaptiveBatch = Math.min(
    Math.floor(workerFinalCount / WORKER_FINALS_PER_ADAPTIVE_BATCH),
    MAX_ADAPTIVE_BATCHES
  );

  useEffect(() => {
    if (
      !isStaff
      || !questionsRef.current.length
      || adaptiveBatch < 1
      || requestedAdaptiveBatchesRef.current.has(adaptiveBatch)
      || isLoadingRef.current
    ) {
      return;
    }
    // The browser only observes existing transcript state. The API never
    // accepts this count and independently proves worker-final progress.
    requestedAdaptiveBatchesRef.current.add(adaptiveBatch);
    void generateQuestions(CONVERSATION_AUTO_INTENT, adaptiveBatch);
  }, [adaptiveBatch, generateQuestions, isStaff, questions.length]);

  if (!isStaff) return null;

  return (
    <section className="live-interview-ai-companion" aria-label="Private AI Companion">
      {error ? <p className="live-interview-error" role="alert">{error}</p> : null}
      {canRetryInitialGeneration ? (
        <button
          type="button"
          className="live-interview-ai-retry-button"
          onClick={() => void generateQuestions(MANUAL_RETRY_INTENT)}
          disabled={isLoading}
        >
          {isLoading ? "Retrying suggestions..." : "Retry suggestions"}
        </button>
      ) : null}
      <section
        className="live-interview-ai-section live-interview-ai-question-section"
        aria-label="Question suggestions"
      >
        <header className="live-interview-ai-section-header">
          <div>
            <h3>Suggested questions</h3>
          </div>
          {isMock ? <span className="live-interview-ai-mock-badge">Mock guidance</span> : null}
        </header>
        {isMock ? (
          <p className="live-interview-ai-helper-text">Mock suggestions — sample guidance only.</p>
        ) : null}
        {isLoading ? (
          <p className="live-interview-transcript-status-label" role="status" aria-live="polite">
            Preparing question suggestions...
          </p>
        ) : null}
        {!isLoading && !questions.length ? (
          <p className="live-interview-ai-helper-text">
            Suggestions will appear when verified interview context is available.
          </p>
        ) : null}
        {questions.length ? (
          <ol
            className="live-interview-ai-question-list live-interview-ai-question-cards"
            aria-label="Suggested interview questions"
          >
            {questions.map((suggestion, index) => (
              <li
                key={suggestion.questionReference || `${suggestion.id}-${suggestion.question}`}
                className="live-interview-ai-question-card"
              >
                <span className="live-interview-ai-question-number" aria-hidden="true">
                  {index + 1}
                </span>
                <div className="live-interview-ai-question-content">
                  {suggestion.category ? (
                    <span className="live-interview-ai-question-category">{suggestion.category}</span>
                  ) : null}
                  <p className="live-interview-ai-question-text">{suggestion.question}</p>
                </div>
              </li>
            ))}
          </ol>
        ) : null}
      </section>
    </section>
  );
};

export default AiCompanionPanel;
